import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { StaticValue } from "../../seed/constants";
import { applyAuthorizationHeader } from "../../services/apiService";
import {
	hasActiveUserAuthorization,
	setAuthorizationToken,
	useAuthorizationToken,
} from "../../services/auth/authToken";
import { clearActiveManagedCompanyId, clearClaimSession } from "../../services/companies/claimSession";
import { useUserAccount } from "../../services/create-resume/queries";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

type UserData = Record<string, any>;

interface SessionInput {
	token: string;
	expiresAt?: string | number | Date;
	userData?: UserData | null;
}

interface AuthContextValue {
	authorizationToken: string | null;
	isLoggedIn: boolean;
	isLoading: boolean;
	logout: () => void;
	refetch: () => unknown;
	setSession: (session: SessionInput) => void;
	userData: UserData | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_SCOPED_QUERY_ROOTS = [
	["user", "account"],
	["resumes"],
	["resume"],
	["smartProfile"],
	["dspInfo"],
	["applications"],
	["jobs", "saved"],
	["jobs", "recommended"],
] as const;

function readStoredUserData(): UserData | null {
	try {
		const storedProfile = localStorage.getItem("profileData");
		return storedProfile ? JSON.parse(storedProfile) : null;
	} catch {
		return null;
	}
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [, , removeCookie] = useCookies();
	const authorizationToken = useAuthorizationToken();
	const [userData, setUserData] = useState<UserData | null>(() =>
		hasActiveUserAuthorization(authorizationToken) ? readStoredUserData() : null,
	);
	const isLoggedIn = hasActiveUserAuthorization(authorizationToken);
	const { data, isLoading, refetch } = useUserAccount();

	const setSession = useCallback(
		({ token, expiresAt, userData: nextUserData }: SessionInput) => {
			if (!token) return;

			clearActiveManagedCompanyId();
			applyAuthorizationHeader(token);
			setAuthorizationToken(token, expiresAt);

			// Retire only data owned by the previous credential. Public job/company queries stay mounted,
			// which keeps an open job and its sign-in sheet intact during an inline authentication flow.
			for (const queryKey of USER_SCOPED_QUERY_ROOTS) {
				queryClient.removeQueries({ queryKey });
			}

			setUserData(hasActiveUserAuthorization(token) ? (nextUserData ?? null) : null);
		},
		[queryClient],
	);

	useEffect(() => {
		if (!isLoggedIn) {
			setUserData(null);
		}
	}, [isLoggedIn]);

	useEffect(() => {
		if (isLoading) return;
		if (data) {
			setUserData(data);
			const isLoggingIn = localStorage.getItem("isLoggingIn");
			if (isLoggingIn) {
				navigate("/profile");
				localStorage.removeItem("isLoggingIn");
			}
		}
	}, [data, isLoading, navigate]);

	useEffect(() => {
		if (!userData) {
			localStorage.removeItem("profileData");
			return;
		}
		localStorage.setItem("profileData", JSON.stringify(userData));
	}, [userData]);

	const logout = useCallback(() => {
		setUserData(null);
		applyAuthorizationHeader(null);
		clearClaimSession();
		// Not every logout path reloads the page — without this the next person to sign in in this
		// tab reads the previous account out of the react-query cache.
		queryClient.clear();
		localStorage.removeItem("profileData");
		localStorage.removeItem("isDropzOpsUser");
		localStorage.removeItem("jobPreferences");
		localStorage.removeItem("isSubscribed");

		// The pointer is written with `path: "/"`, and a removal has to name the same
		// path or it targets the current directory instead and the cookie survives —
		// leaving the next person in this tab pointed at the previous resume.
		removeCookie(staticValues.localStorageResumeId, { path: "/" });

		const allLocalStorageKeys = Object.keys(localStorage);
		allLocalStorageKeys.forEach((key) => {
			if (key.includes("resume")) {
				localStorage.removeItem(key);
			}
		});
	}, [queryClient, removeCookie]);

	const value = useMemo(
		() => ({
			authorizationToken,
			isLoggedIn,
			isLoading,
			logout,
			refetch,
			setSession,
			userData,
		}),
		[authorizationToken, isLoggedIn, isLoading, logout, refetch, setSession, userData],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
